export function MarketingTips() {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Marketing Your Apartment Building Effectively
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Effective marketing is crucial among tips for selling an apartment building. Unlike residential properties, 
            apartment buildings require targeted marketing to reach qualified investors and institutional buyers. 
            Learn how to create compelling marketing materials and reach the right audience for your property.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">Create a Professional Offering Memorandum</h3>
            <p className="text-gray-600 mb-6">
              The offering memorandum (OM) is your property's resume and one of the most important marketing tools 
              when selling an apartment building. This comprehensive document provides potential buyers with all 
              the information they need to evaluate your property and make informed decisions. A well-prepared OM 
              demonstrates professionalism and can significantly impact buyer interest and offer quality.
            </p>
            <div className="space-y-4">
              <div className="bg-gray-50 p-5 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                  <i className="ri-file-list-line text-orange-500 mr-2"></i>
                  Essential OM Components
                </h4>
                <ul className="text-gray-600 space-y-2 text-sm">
                  <li>• Executive summary with property highlights</li>
                  <li>• Detailed property description and specifications</li>
                  <li>• High-quality professional photography</li>
                  <li>• Unit mix breakdown and floor plans</li>
                  <li>• Current rent roll and lease abstracts</li>
                  <li>• 3-year financial performance history</li>
                  <li>• Market analysis and demographics</li>
                  <li>• Capital improvements and deferred maintenance</li>
                  <li>• Proforma projections and value-add opportunities</li>
                  <li>• Location maps and area amenities</li>
                </ul>
              </div>
              <div className="bg-orange-50 p-5 rounded-lg border-l-4 border-orange-500">
                <p className="text-sm text-gray-700">
                  <strong className="text-gray-900">Pro Tip:</strong> Hire a professional designer to create your OM. 
                  A polished, visually appealing document signals that you're a serious seller with a quality property, 
                  which can justify premium pricing and attract better-qualified buyers.
                </p>
              </div>
            </div>
          </div>
          <div>
            <img 
              src="https://public.readdy.ai/ai/img_res/acdeb502f3d7f873e801770ff780f07f.jpg"
              alt="Professional offering memorandum"
              className="w-full h-96 object-cover rounded-xl shadow-lg"
            />
          </div>
        </div>

        <div className="bg-gray-50 rounded-xl p-12 mb-16">
          <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Multi-Channel Marketing Strategy
          </h3>
          <p className="text-gray-600 text-center mb-12 max-w-3xl mx-auto">
            One of the key tips for selling an apartment building is using multiple marketing channels to reach 
            different types of buyers. Each channel attracts different investor profiles, so a comprehensive 
            approach maximizes your exposure and increases the likelihood of receiving multiple competitive offers.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-global-line text-2xl text-green-600"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Online Platforms</h4>
              <p className="text-gray-600 text-sm mb-3">
                List on major commercial real estate platforms like LoopNet, CoStar, Crexi, and Ten-X to reach 
                active investors searching for properties.
              </p>
              <ul className="text-gray-600 text-xs space-y-1">
                <li>• LoopNet (largest reach)</li>
                <li>• CoStar (institutional buyers)</li>
                <li>• Crexi (emerging platform)</li>
                <li>• Ten-X (auction option)</li>
              </ul>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-user-line text-2xl text-orange-600"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Broker Networks</h4>
              <p className="text-gray-600 text-sm mb-3">
                Work with commercial real estate brokers who have established relationships with qualified 
                apartment building investors.
              </p>
              <ul className="text-gray-600 text-xs space-y-1">
                <li>• Access to buyer databases</li>
                <li>• Off-market opportunities</li>
                <li>• Negotiation expertise</li>
                <li>• Transaction management</li>
              </ul>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-mail-line text-2xl text-red-600"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Direct Marketing</h4>
              <p className="text-gray-600 text-sm mb-3">
                Target known apartment building investors in your market with personalized outreach and 
                property information.
              </p>
              <ul className="text-gray-600 text-xs space-y-1">
                <li>• Email campaigns to investors</li>
                <li>• Direct mail to property owners</li>
                <li>• LinkedIn outreach</li>
                <li>• Investment group presentations</li>
              </ul>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-group-line text-2xl text-indigo-600"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Networking Events</h4>
              <p className="text-gray-600 text-sm mb-3">
                Attend real estate investment meetings, conferences, and local investor groups to connect 
                with potential buyers.
              </p>
              <ul className="text-gray-600 text-xs space-y-1">
                <li>• Local REIA meetings</li>
                <li>• Commercial real estate conferences</li>
                <li>• Industry trade shows</li>
                <li>• Investor networking events</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <img 
              src="https://readdy.ai/api/search-image?query=professional%20real%20estate%20photographer%20taking%20high%20quality%20photos%20of%20apartment%20building%20exterior%20and%20interior%20modern%20camera%20equipment%20drone%20photography%20commercial%20property%20marketing%20beautiful%20architectural%20photography&width=800&height=600&seq=tips-marketing-002&orientation=landscape"
              alt="Professional property photography"
              className="w-full h-96 object-cover rounded-xl shadow-lg"
            />
          </div>
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">Invest in Professional Photography and Media</h3>
            <p className="text-gray-600 mb-6">
              High-quality visual content is non-negotiable when marketing apartment buildings. Professional 
              photography and media can increase buyer interest by 60% or more and help your property stand out 
              in crowded online listings. This investment typically costs $1,000-3,000 but can add tens of 
              thousands to your sale price by attracting more buyers and creating stronger first impressions.
            </p>
            <div className="space-y-4">
              <div className="flex items-start">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-camera-line text-green-600"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Professional Photography</h4>
                  <p className="text-gray-600 text-sm">
                    Hire a commercial real estate photographer who understands how to showcase multifamily 
                    properties. Include exterior shots from multiple angles, common areas, representative units, 
                    and amenities during optimal lighting conditions.
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-flight-takeoff-line text-orange-600"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Aerial Drone Footage</h4>
                  <p className="text-gray-600 text-sm">
                    Drone photography provides unique perspectives that showcase your property's size, location, 
                    and surrounding area. Aerial shots are particularly effective for larger complexes and 
                    properties with attractive locations or amenities.
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-video-line text-red-600"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Video Tours</h4>
                  <p className="text-gray-600 text-sm">
                    Create professional video tours that allow out-of-state investors to virtually walk through 
                    your property. Videos increase engagement and help serious buyers pre-qualify themselves 
                    before scheduling in-person showings.
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-map-line text-indigo-600"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Virtual Tours and 3D Walkthroughs</h4>
                  <p className="text-gray-600 text-sm">
                    Matterport or similar 3D tours allow buyers to explore your property remotely at their 
                    convenience. This technology is especially valuable for attracting out-of-market investors 
                    and reducing unnecessary showings.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-orange-50 rounded-xl p-12">
          <h3 className="text-3xl font-bold text-gray-900 mb-6 text-center">
            Confidentiality and Discretion
          </h3>
          <p className="text-gray-600 text-center mb-8 max-w-3xl mx-auto">
            An often-overlooked aspect of tips for selling an apartment building is maintaining confidentiality 
            during the marketing process. Improper disclosure can upset tenants, alert competitors, and create 
            unnecessary complications. Implement these strategies to protect your interests while marketing effectively.
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                <i className="ri-shield-check-line text-green-600 mr-2"></i>
                Use Blind Listings
              </h4>
              <p className="text-gray-600 text-sm">
                Initially market your property without revealing the exact address. Provide general location 
                information and require interested buyers to sign confidentiality agreements before receiving 
                detailed property information and the specific address.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                <i className="ri-file-shield-line text-orange-600 mr-2"></i>
                Require NDAs
              </h4>
              <p className="text-gray-600 text-sm">
                Have all potential buyers sign Non-Disclosure Agreements before sharing sensitive financial 
                information, tenant details, or operational data. This protects your business information and 
                ensures serious buyer intent.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                <i className="ri-user-voice-line text-red-600 mr-2"></i>
                Communicate with Tenants
              </h4>
              <p className="text-gray-600 text-sm">
                Decide when and how to inform tenants about the sale. Many sellers wait until they have a 
                contract to avoid unnecessary concern. When you do notify tenants, reassure them about lease 
                terms and continuity of service.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}